<template>
  <DataTable
    :value="loyaltyLevels"
    responsive-layout="scroll"
    class="loyalty-table"
    :selection-mode="'single'"
    :data-selected-column="selectedColumn"
  >
    <Column field="benefit" header="" class="benefit-column"></Column>
    <Column
      v-for="level in levelNames"
      :key="level"
      :field="level"
      :header="level"
      class="level-column"
    >
      <template #body="slotProps">
        <button class="level-column-button" @click="selectColumn(level)">
          <template v-if="typeof slotProps.data[level] === 'boolean'">
            <i v-if="slotProps.data[level]" class="circle-dot"></i>
          </template>
          <template v-else>
            {{ slotProps.data[level] }}
          </template>
        </button>
      </template>
    </Column>
  </DataTable>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import loyaltyTiers from "@/assets/json/loyalty-tiers.json";
import { mapGetters } from "vuex";

export default {
  name: "LoyaltyTable",
  components: {
    DataTable,
    Column,
  },
  data() {
    return {
      loyaltyTiersJson: loyaltyTiers,
      selectedRow: null,
      selectedColumn: null,
    };
  },
  computed: {
    ...mapGetters("app", ["currencyCode"]),
    levelNames() {
      return this.loyaltyTiersJson?.tiers?.map((tier) => {
        return tier.name;
      });
    },
    loyaltyLevels() {
      if (!this.currencyCode || !this.loyaltyTiersJson?.tiers?.length)
        return [];

      const maxLevels = this.loyaltyTiersJson.tiers.length;
      const levelKeys = Array.from(
        { length: maxLevels },
        (_, i) => `LEVEL ${i + 1}`
      );

      // Helper function to get currency formatted string
      const getTierBoundaryString = (tier, index) => {
        const lowerBound = this.$filters.toCurrency(
          tier.lowerBound[this.currencyCode],
          this.currencyCode,
          0
        );
        const upperBound = tier.upperBound?.[this.currencyCode]
          ? this.$filters.toCurrency(
              tier.upperBound[this.currencyCode],
              this.currencyCode,
              0
            )
          : `${lowerBound}+`;
        return index === maxLevels - 1
          ? `${lowerBound}+`
          : `${lowerBound} - ${upperBound}`;
      };

      // Helper function to get formatted birthday gift amount
      const getBirthdayGiftString = (tier) => {
        return this.$filters.toCurrency(
          tier.birthdayBonus?.[this.currencyCode] || 0,
          this.currencyCode,
          0
        );
      };

      const birthdayGiftsMap = this.loyaltyTiersJson.tiers.map((tier) => {
        return getBirthdayGiftString(tier);
      });

      // Level Thresholds
      const levelThresholds = {
        benefit: "Level Threshold",
        ...this.loyaltyTiersJson.tiers.reduce((acc, tier, index) => {
          const levelKey = `LEVEL ${index + 1}`;
          acc[levelKey] = getTierBoundaryString(tier, index);
          return acc;
        }, {}),
      };

      // Benefits configuration
      const benefits = [
        {
          benefit: "Earn Points As You Shop",
          levels: [true, true, true, true],
        },
        {
          benefit: "Priority Sale Access",
          levels: ["1 hr early", "2 hr early", "3 hr early", "4 hr early"],
        },
        {
          benefit: "Early Collection Access",
          levels: ["1 hr early", "2 hr early", "3 hr early", "4 hr early"],
        },
        {
          benefit: "Birthday Gift",
          levels: birthdayGiftsMap,
        },
        {
          benefit: "VIP Customer Support",
          levels: [false, false, true, true],
        },
        {
          benefit: "Free Premium Shipping",
          levels: [false, false, true, true],
        },
        {
          benefit: "Free Returns",
          levels: [false, false, false, true],
        },
        {
          benefit: "Double Points on All Purchases",
          levels: [false, false, false, true],
        },
      ];

      // Map benefits into desired structure
      const mappedBenefits = benefits.map((benefit) => {
        const benefitLevels = levelKeys.reduce((acc, level, index) => {
          acc[level] = benefit.levels[index] ?? false;
          return acc;
        }, {});
        return { benefit: benefit.benefit, ...benefitLevels };
      });

      // Combine level thresholds and benefits
      return [levelThresholds, ...mappedBenefits];
    },
  },
  methods: {
    selectColumn(level) {
      this.selectedColumn = level;
    },
  },
};
</script>

<style lang="scss" scoped>
$levels: ("LEVEL 1", "LEVEL 2", "LEVEL 3", "LEVEL 4");

.loyalty-table {
  width: 100%;

  :deep(.p-datatable-wrapper) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  :deep(.p-datatable-table) {
    min-width: 120%;
  }

  :deep(.benefit-column) {
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: white;
    min-width: 150px;
    width: auto;
  }

  :deep(.level-column) {
    min-width: 120px;
    width: auto;
  }

  :deep(.p-datatable-thead > tr > th) {
    border-bottom: 1px solid #e4e4e4;
  }

  :deep(.p-datatable-tbody > tr) {
    height: 60px;
    border-bottom: 1px solid #e4e4e4;
  }

  :deep(.p-datatable-tbody > tr > td) {
    vertical-align: middle;
    border-bottom: 1px solid #e4e4e4;
    @include editorial-body(14px);
  }

  :deep(.p-datatable-column-header-content) {
    align-items: center;
    justify-content: center;
    @include editorial-body(14px);
    text-transform: uppercase;
    height: 80px;
  }

  :deep(.benefit-column) {
    text-align: left;
  }

  :deep(.level-column) {
    text-align: center;
  }

  @each $level in $levels {
    &[data-selected-column="#{$level}"] {
      :deep(.p-datatable-thead
          > tr
          > th:nth-child(#{index($levels, $level) + 1})),
      :deep(.p-datatable-tbody
          > tr
          > td:nth-child(#{index($levels, $level) + 1})) {
        background-color: #f8f8f8;
      }
    }
  }

  @include respond-above(md) {
    :deep(.p-datatable-table) {
      min-width: 100%;
    }

    :deep(.benefit-column) {
      position: static;
      width: 25%;
      min-width: auto;
    }

    :deep(.level-column) {
      width: 18.75%;
      min-width: auto;
    }
  }
}

@include respond-above(lg) {
  :deep(.p-datatable-table) {
    min-width: 150%;
  }
}

.circle-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #000;
  margin: 0 auto;
}

.level-column-button {
  background: none;
  border: none;
  width: 100%;
  height: 60px;
  cursor: pointer;
}
</style>
